import { ColumnDef } from '@tanstack/react-table';
import { Loader2, Package, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { OrderItemCreateInput, RENTAL_LENGTH_OPTIONS } from '../../types/orderItem';
import { ProductThumbnail } from '../ui/aspect-ratio-image';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { DataTable } from '../ui/data-table';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';

export type AddItemsStepProps = {
  items: OrderItemCreateInput[];
  onChange: (items: OrderItemCreateInput[]) => void;
};

type AssetTemplate = {
  id: string;
  name: string;
  description?: string;
  base_price?: number;
  image_url?: string;
  available_units_count?: number;
  images?: Array<{
    id: string;
    url?: string | null;
    variant: 'web' | 'hires';
    position: number;
  }>;
};

type ProductConfig = {
  quantity: number;
  rental_length_months: number;
  unit_price: number;
};

const AddItemsStep = ({ items, onChange }: AddItemsStepProps) => {
  // Product selection state
  const [availableProducts, setAvailableProducts] = useState<AssetTemplate[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [productsLimit, setProductsLimit] = useState(10);
  const [productsTotalPages, setProductsTotalPages] = useState(1);
  const [productsTotalItems, setProductsTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Configuration state for selected products
  const [configuredItems, setConfiguredItems] = useState<Record<string, ProductConfig>>({});

  useEffect(() => {
    loadProducts(productsPage, productsLimit, searchTerm);
  }, [productsPage, productsLimit, searchTerm]);

  const loadProducts = async (page = productsPage, limit = productsLimit, search?: string) => {
    try {
      setLoadingProducts(true);
      const token = localStorage.getItem('token');
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`/api/asset-templates?page=${page}&limit=${limit}${searchParam}&has_available_units=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();

      if (result.success && result.pagination) {
        setAvailableProducts(result.data || []);
        setProductsTotalItems(result.pagination.total);
        setProductsTotalPages(result.pagination.pages);
      } else {
        setAvailableProducts(result.data || []);
        setProductsTotalItems((result.data || []).length);
        setProductsTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductSelect = (productId: string, checked: boolean) => {
    if (checked) {
      setSelectedProductIds([...selectedProductIds, productId]);
      // Initialize configuration with defaults
      const product = availableProducts.find(p => p.id === productId);
      if (product && !configuredItems[productId]) {
        setConfiguredItems({
          ...configuredItems,
          [productId]: {
            quantity: 1,
            rental_length_months: 1,
            unit_price: product.base_price || 0,
          },
        });
      }
    } else {
      setSelectedProductIds(selectedProductIds.filter(id => id !== productId));
      // Keep configuration in case user re-selects
    }
  };

  const handleConfigChange = (productId: string, field: keyof ProductConfig, value: number) => {
    setConfiguredItems({
      ...configuredItems,
      [productId]: {
        ...configuredItems[productId],
        [field]: value,
      },
    });
  };

  const addConfiguredItems = () => {
    if (selectedProductIds.length === 0) {
      alert('Please select at least one product');
      return;
    }

    const newItems: OrderItemCreateInput[] = selectedProductIds.map(productId => {
      const product = availableProducts.find(p => p.id === productId) ||
                     items.find(i => i.asset_template_id === productId) as any;
      const config = configuredItems[productId];
      const primaryImage = product?.images?.find((img: any) => img.variant === 'web');
      const imageUrl = primaryImage?.url || product?.images?.[0]?.url || product?.image_url;

      return {
        asset_template_id: productId,
        quantity: config?.quantity || 1,
        rental_length_months: config?.rental_length_months || 1,
        unit_price: config?.unit_price || 0,
        _templateName: product?.name,
        _imageUrl: imageUrl,
      } as OrderItemCreateInput;
    });

    onChange([...items, ...newItems]);

    // Clear selection and configuration
    setSelectedProductIds([]);
    setConfiguredItems({});
  };

  const clearSelection = () => {
    setSelectedProductIds([]);
    setConfiguredItems({});
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const getRentalLabel = (months: number) => {
    return RENTAL_LENGTH_OPTIONS.find(opt => opt.value === months)?.label || `${months} months`;
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price * item.rental_length_months), 0);
  };

  const getProductById = (id: string): AssetTemplate | undefined => {
    return availableProducts.find(p => p.id === id);
  };

  // Custom search handler for server-side search
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setProductsPage(1); // Reset to first page when searching
  };

  const productColumns: ColumnDef<AssetTemplate>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
            if (value) {
              const pageProductIds = availableProducts.map(p => p.id);
              const combined = [...selectedProductIds, ...pageProductIds];
              const newSelected = Array.from(new Set(combined));
              setSelectedProductIds(newSelected);
              // Initialize configs for new selections
              const newConfigs = { ...configuredItems };
              pageProductIds.forEach(id => {
                if (!newConfigs[id]) {
                  const product = availableProducts.find(p => p.id === id);
                  newConfigs[id] = {
                    quantity: 1,
                    rental_length_months: 1,
                    unit_price: product?.base_price || 0,
                  };
                }
              });
              setConfiguredItems(newConfigs);
            } else {
              const pageProductIds = availableProducts.map(p => p.id);
              setSelectedProductIds(selectedProductIds.filter(id => !pageProductIds.includes(id)));
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedProductIds.includes(row.original.id)}
          onCheckedChange={(value) => handleProductSelect(row.original.id, !!value)}
          aria-label="Select row"
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'Product',
      cell: ({ row }) => {
        const product = row.original;
        const primaryImage = product.images?.find(img => img.variant === 'web');
        const imageUrl = primaryImage?.url || product.images?.[0]?.url;

        return (
          <div className="flex items-center gap-3">
            <ProductThumbnail
              src={imageUrl}
              alt={product.name}
              size="sm"
              fallback={<Package className="h-6 w-6 text-muted-foreground" />}
            />
            <span className="font-medium">{product.name}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'base_price',
      header: 'Base Price',
      cell: ({ row }) => {
        const price = row.getValue('base_price') as number;
        return price ? (
          <span className="font-medium">£{formatCurrency(price)}/month</span>
        ) : (
          <span className="text-gray-400">—</span>
        );
      },
    },
    {
      accessorKey: 'available_units_count',
      header: 'Available Units',
      cell: ({ row }) => {
        const count = row.getValue('available_units_count') as number;
        return (
          <span className={`font-medium ${count > 10 ? 'text-green-600' : count > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
            {count || 0}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Product Selection Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Select Products</h3>
        {loadingProducts && productsPage === 1 && searchTerm === '' ? (
          <div className="flex items-center justify-center py-8 border rounded-md">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading products...</span>
          </div>
        ) : (
          <DataTable
            columns={productColumns}
            data={availableProducts}
            emptyMessage="No products found"
            loading={loadingProducts}
            serverSide={true}
            pagination={{
              total: productsTotalItems,
              page: productsPage,
              limit: productsLimit,
              pages: productsTotalPages,
            }}
            onPaginationChange={(newPage, newLimit) => {
              if (newLimit !== productsLimit) {
                setProductsLimit(newLimit);
                setProductsPage(1);
              } else {
                setProductsPage(newPage);
              }
            }}
            toolbarContentLeft={
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products or categories..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-8"
                />
              </div>
            }
          />
        )}
      </div>

      {/* Configuration Section */}
      {selectedProductIds.length > 0 && (
        <>
          <Separator />
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Configure Selected Products ({selectedProductIds.length})</h3>
              <Button variant="outline" size="sm" onClick={clearSelection}>
                <X className="h-4 w-4 mr-2" />
                Clear Selection
              </Button>
            </div>

            <div className="space-y-3">
              {selectedProductIds.map(productId => {
                const product = getProductById(productId);
                const config = configuredItems[productId] || { quantity: 1, rental_length_months: 1, unit_price: 0 };
                const primaryImage = product?.images?.find(img => img.variant === 'web');
                const imageUrl = primaryImage?.url || product?.images?.[0]?.url;

                return (
                  <Card key={productId}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Product Image */}
                        <ProductThumbnail
                          src={imageUrl}
                          alt={product?.name || 'Product'}
                          size="md"
                          fallback={<Package className="h-8 w-8 text-muted-foreground" />}
                        />

                        {/* Product Details and Configuration */}
                        <div className="flex-1 space-y-3">
                          <div>
                            <h4 className="font-medium">{product?.name}</h4>
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label htmlFor={`qty-${productId}`}>Quantity</Label>
                              <Input
                                id={`qty-${productId}`}
                                type="number"
                                min="1"
                                value={config.quantity}
                                onChange={(e) => handleConfigChange(productId, 'quantity', parseInt(e.target.value) || 1)}
                              />
                            </div>

                            <div>
                              <Label htmlFor={`rental-${productId}`}>Rental Length</Label>
                              <Select
                                value={config.rental_length_months.toString()}
                                onValueChange={(value) => handleConfigChange(productId, 'rental_length_months', parseInt(value))}
                              >
                                <SelectTrigger id={`rental-${productId}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {RENTAL_LENGTH_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value.toString()}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label htmlFor={`price-${productId}`}>Price (£/month)</Label>
                              <Input
                                id={`price-${productId}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={formatCurrency(config.unit_price)}
                                onChange={(e) => handleConfigChange(productId, 'unit_price', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>

                          <div className="text-sm text-muted-foreground">
                            Subtotal: <span className="font-semibold text-foreground">
                              £{formatCurrency(config.quantity * config.unit_price * config.rental_length_months)}
                            </span> ({getRentalLabel(config.rental_length_months)})
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Button onClick={addConfiguredItems} className="w-full mt-4" size="lg">
              <Plus className="h-4 w-4 mr-2" />
              Add {selectedProductIds.length} Product{selectedProductIds.length !== 1 ? 's' : ''} to Order
            </Button>
          </div>
        </>
      )}

      {/* Order Items List */}
      {items.length > 0 && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Order Items</h3>
              <span className="text-sm text-gray-500">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
            </div>

            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Product Image */}
                    <ProductThumbnail
                      src={(item as any)._imageUrl}
                      alt={(item as any)._templateName || 'Product'}
                      size="md"
                      fallback={<Package className="h-8 w-8 text-muted-foreground" />}
                    />

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-base">{(item as any)._templateName}</div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                        <span>Qty: {item.quantity}</span>
                        <span>•</span>
                        <span>{getRentalLabel(item.rental_length_months)}</span>
                        <span>•</span>
                        <span>£{formatCurrency(item.unit_price)}/month</span>
                      </div>
                    </div>

                    {/* Price and Actions */}
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <div className="font-bold text-lg">£{formatCurrency(item.quantity * item.unit_price * item.rental_length_months)}</div>
                        <div className="text-xs text-gray-500">Total ({item.rental_length_months} months)</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center p-4 bg-primary/5 rounded-lg">
              <span className="font-semibold text-lg">Order Subtotal</span>
              <span className="font-bold text-2xl text-primary">£{formatCurrency(calculateTotal())}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddItemsStep;
